/**
 * Shared types for the Manufacturing Management Transfers workbench. Mirrors
 * the JSON shapes returned by `GET /api/manufacturing/transfers/board`
 * (registered in `src/api/manufacturing-transfers-routes.ts`).
 */

export const TRANSFER_ORDER_STATUS_VALUES = [
  "pending",
  "packing",
  "ready_to_ship",
  "shipped",
  "delivered",
  "partially_received",
  "received",
  "cancelled",
] as const;
export type TransferOrderStatus = (typeof TRANSFER_ORDER_STATUS_VALUES)[number];

export const TRANSFER_ORDER_STATUS_LABELS: Readonly<Record<TransferOrderStatus, string>> = {
  pending: "Pending",
  packing: "Packing",
  ready_to_ship: "Ready to ship",
  shipped: "Shipped",
  delivered: "Delivered",
  partially_received: "Partially received",
  received: "Received",
  cancelled: "Cancelled",
};

export const TRANSFER_ORDER_TERMINAL_STATUSES: ReadonlySet<TransferOrderStatus> = new Set<TransferOrderStatus>([
  "received",
  "cancelled",
]);

import { MRP_LOCATION_PUBLIC_LOCATION_ID } from "@/lib/mrp";

export const TRANSFER_SGS_ORIGIN_LOCATION_ID = MRP_LOCATION_PUBLIC_LOCATION_ID.sgs;

export function transferOrderIsSgsOrigin(order: { origin_location_id: number }): boolean {
  return order.origin_location_id === TRANSFER_SGS_ORIGIN_LOCATION_ID;
}

export function transferOrderShowsOutboundLink(order: {
  status: TransferOrderStatus;
  origin_location_id: number;
}): boolean {
  return order.status === "ready_to_ship" && transferOrderIsSgsOrigin(order);
}

export function transferOrderShowsPrePackLink(order: {
  status: TransferOrderStatus;
  origin_location_id: number;
}): boolean {
  return order.status === "packing" && transferOrderIsSgsOrigin(order);
}

export function transferOrderShowsTransferOrderPdfLink(order: {
  status: TransferOrderStatus;
  origin_location_id: number;
}): boolean {
  if (transferOrderIsSgsOrigin(order)) return false;
  return order.status === "pending" || order.status === "packing" || order.status === "ready_to_ship";
}

/** Carrier and tracking are captured at ship time (outbound hub or mark shipped). */
export const TRANSFER_ORDER_POST_SHIP_STATUSES: ReadonlySet<TransferOrderStatus> = new Set<TransferOrderStatus>([
  "shipped",
  "delivered",
  "partially_received",
  "received",
]);

export function transferOrderShowsEditTracking(order: { status: TransferOrderStatus }): boolean {
  return TRANSFER_ORDER_POST_SHIP_STATUSES.has(order.status);
}

/**
 * Forward-progress action for the workbench primary button. Non-SGS origins ship
 * directly from packing (external sites pack outside hub pre-pack). SGS-origin
 * packing advances to ready_to_ship only from pre-pack when cartons are sealed.
 */
export function primaryNextActionForTransferOrder(order: {
  status: TransferOrderStatus;
  origin_location_id: number;
}): { to: TransferOrderStatus; label: string } | null {
  switch (order.status) {
    case "pending":
      return { to: "packing", label: "Lock for packing" };
    case "packing":
      if (!transferOrderIsSgsOrigin(order)) {
        return { to: "shipped", label: "Mark shipped" };
      }
      return null;
    case "ready_to_ship":
      return { to: "shipped", label: "Mark shipped" };
    case "shipped":
      return { to: "delivered", label: "Mark delivered" };
    default:
      return null;
  }
}

export function transferOrderPrePackHref(order: { id: number; transfer_number: string | null }): string {
  return `/shipping/pre-pack/transfers/${String(order.id)}`;
}

export const VARIANCE_REASON_VALUES = [
  "miscount_ours",
  "miscount_theirs",
  "ups_loss",
  "damaged",
  "other",
] as const;
export type VarianceReason = (typeof VARIANCE_REASON_VALUES)[number];

export const VARIANCE_REASON_LABELS: Readonly<Record<VarianceReason, string>> = {
  miscount_ours: "Miscount (ours)",
  miscount_theirs: "Miscount (theirs)",
  ups_loss: "UPS loss",
  damaged: "Damaged",
  other: "Other",
};

export interface TransferBoardRequest {
  id: string;
  sku_id: string;
  origin_location_id: number;
  destination_location_id: number;
  target_date: string;
  requested_quantity: number;
  allocated_quantity: number;
  remaining_quantity: number;
  reason: string;
  notes: string | null;
  status: "open" | "partially_allocated";
  created_by: string;
  created_by_display_name: string;
  created_at: string;
  updated_at: string;
}

export interface TransferBoardOrderLine {
  id: number;
  sku_id: string | null;
  description: string | null;
  quantity: number;
  approved_pack_quantity: number | null;
  received_quantity: number | null;
  return_to_origin_quantity: number | null;
  lost_quantity: number | null;
  line_number: number | null;
  reason: string;
  fulfilled_transfer_request_id: string | null;
}

export type TransferPackingQuantityRequestStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"
  | "superseded";

export interface TransferBoardPackingQuantityRequest {
  id: string;
  transfer_order_id: number;
  transfer_order_item_id: number;
  ordered_quantity: number;
  requested_quantity: number;
  origin_on_hand_quantity: number | null;
  inventory_adjustment_quantity: number | null;
  reason: string | null;
  status: TransferPackingQuantityRequestStatus;
  requested_by_user_id: string;
  requested_by_display_name: string;
  requested_at: string;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolution_note: string | null;
}

export interface TransferBoardBox {
  id: number;
  box_number: number;
  length_in: number | null;
  width_in: number | null;
  height_in: number | null;
  weight_lbs: number | null;
  tracking_number: string | null;
  sealed_at: string | null;
  label_printed_at: string | null;
  created_at: string;
}

export interface TransferBoardAllocation {
  id: string;
  transfer_order_item_id: number;
  pack_list_id: number;
  quantity: number;
  created_at: string;
}

export interface TransferBoardDiscrepancy {
  id: number;
  transfer_order_item_id: number;
  transfer_receive_event_id: string | null;
  sku_id: string | null;
  expected_qty: number;
  received_qty: number;
  return_to_origin_qty: number | null;
  lost_qty: number | null;
  remaining_qty: number | null;
  variance: number;
  variance_reason: string | null;
  notes: string | null;
  resolved: boolean;
  created_at: string;
}

export interface TransferBoardReceiveEvent {
  id: string;
  received_at: string;
  received_by: string | null;
  notes: string | null;
  create_follow_up_transfer: boolean | null;
  follow_up_transfer_order_id: number | null;
}

export interface TransferBoardShipment {
  id: number;
  carrier: string;
  carrier_name: string | null;
  carrier_shipment_id: string | null;
  shipped_at: string;
  delivered_at: string | null;
}

export type TransferMvDocRole =
  | "out_of_origin"
  | "into_destination"
  | "cancel_reverse"
  | "return_to_origin"
  | "wastage";

export const TRANSFER_MV_DOC_ROLE_LABELS: Readonly<Record<TransferMvDocRole, string>> = {
  out_of_origin: "Ship: origin → in-transit",
  into_destination: "Receive: in-transit → destination",
  cancel_reverse: "Cancel: in-transit → origin",
  return_to_origin: "Return: in-transit → origin",
  wastage: "Lost: wastage- on in-transit",
};

export interface TransferBoardMvDoc {
  id: string;
  role: TransferMvDocRole;
  mv_transfer_id: string;
  mv_transfer_document_number: string | null;
  origin_mv_location_id: number;
  destination_mv_location_id: number;
  quantity_json: unknown;
  idempotency_key: string;
  created_at: string;
  created_by: string | null;
}

export interface TransferBoardSkuPick {
  sku_id: string;
  picked_at: string;
  picked_quantity: number;
}

export interface TransferBoardOrder {
  id: number;
  transfer_number: string | null;
  origin_location_id: number;
  destination_location_id: number;
  ship_by_date: string | null;
  status: TransferOrderStatus;
  notes: string | null;
  access_token: string | null;
  mv_sync_pending: boolean;
  mv_sync_error: string | null;
  packing_started_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  received_at: string | null;
  purchase_order_id: number | null;
  purchase_order_number: string | null;
  /** True when the linked PO has at least one submitted/posted hub receipt (Layer 4). */
  purchase_order_received: boolean;
  created_at: string;
  updated_at: string;
  outbound_carrier: string | null;
  outbound_tracking: string | null;
  shipped_notification_freshdesk_ticket_id: number | null;
  shipped_notification_url: string | null;
  first_picked_at: string | null;
  last_picked_at: string | null;
  sku_picks: TransferBoardSkuPick[];
  lines: TransferBoardOrderLine[];
  boxes: TransferBoardBox[];
  allocations: TransferBoardAllocation[];
  discrepancies: TransferBoardDiscrepancy[];
  receive_events: TransferBoardReceiveEvent[];
  shipments: TransferBoardShipment[];
  mv_docs: TransferBoardMvDoc[];
  packing_quantity_requests: TransferBoardPackingQuantityRequest[];
}

export interface TransferBoardLocation {
  id: number;
  name: string | null;
  abbreviation: string | null;
  mrp_site_code: string | null;
  ship_country_code?: string | null;
  ship_state?: string | null;
}

export interface TransferBoardResponse {
  requests: TransferBoardRequest[];
  orders: TransferBoardOrder[];
  locations: TransferBoardLocation[];
  default_hidden_statuses: TransferOrderStatus[];
}

export function transferOrderStatusBadgeTone(status: TransferOrderStatus): "neutral" | "warning" | "info" | "success" | "danger" | "muted" {
  switch (status) {
    case "pending":
      return "warning";
    case "packing":
      return "info";
    case "ready_to_ship":
      return "info";
    case "shipped":
      return "info";
    case "delivered":
      return "success";
    case "partially_received":
      return "warning";
    case "received":
      return "success";
    case "cancelled":
      return "muted";
  }
}

export function effectiveTransferPackQuantity(line: {
  quantity: number;
  approved_pack_quantity: number | null;
}): number {
  if (
    line.approved_pack_quantity != null
    && Number.isInteger(line.approved_pack_quantity)
    && line.approved_pack_quantity > 0
  ) {
    return line.approved_pack_quantity;
  }
  return line.quantity;
}

/** True when an approver set a pack target that differs from the original ordered quantity. */
export function transferLineHasApprovedPackQuantityDeviation(line: {
  quantity: number;
  approved_pack_quantity: number | null;
}): boolean {
  return (
    line.approved_pack_quantity != null
    && Number.isInteger(line.approved_pack_quantity)
    && line.approved_pack_quantity > 0
    && line.approved_pack_quantity !== line.quantity
  );
}

export type TransferLinePackQuantityBadge = "discrepancy" | "approved";

/** Pre-pack line badge: pending deviation first, then approved deviation. */
export function transferLinePackQuantityBadge(line: {
  quantity: number;
  approved_pack_quantity: number | null;
  has_pending_pack_qty_request: boolean;
}): TransferLinePackQuantityBadge | null {
  if (line.has_pending_pack_qty_request) {
    return "discrepancy";
  }
  if (transferLineHasApprovedPackQuantityDeviation(line)) {
    return "approved";
  }
  return null;
}

export function countPendingTransferPackingQuantityRequests(
  order: Pick<TransferBoardOrder, "packing_quantity_requests">,
): number {
  return order.packing_quantity_requests?.filter((row) => row.status === "pending").length ?? 0;
}

export const TRANSFER_PENDING_QUANTITY_CHANGE_HOVER =
  "A packing quantity change is waiting for approval. This transfer cannot ship until it is resolved.";

export const TRANSFER_INVENTORY_SHORTFALL_CHIP_LABEL = "Not enough inventory";

export const TRANSFER_INVENTORY_SHORTFALL_HOVER =
  "Origin site on-hand is below the quantity this transfer still needs to ship.";

export function transferPendingQuantityChangeChipLabel(count: number): string {
  if (count <= 0) return "";
  if (count === 1) return "Quantity change pending";
  return `${String(count)} quantity changes pending`;
}

export function transferOrderHasSealedCartons(
  order: Pick<TransferBoardOrder, "boxes">,
): boolean {
  return order.boxes.some((box) => box.sealed_at != null);
}

export function transferOrderAllCartonsSealed(
  order: Pick<TransferBoardOrder, "boxes" | "allocations">,
): boolean {
  const packListIdsWithAllocations = new Set<number>();
  for (const allocation of order.allocations) {
    packListIdsWithAllocations.add(Number(allocation.pack_list_id));
  }
  if (packListIdsWithAllocations.size === 0) {
    return false;
  }
  for (const box of order.boxes) {
    if (!packListIdsWithAllocations.has(Number(box.id))) {
      continue;
    }
    const sealedAt = box.sealed_at?.trim() ?? "";
    if (sealedAt === "") {
      return false;
    }
  }
  return true;
}

export function transferOrderPackedQuantityByItemId(
  order: Pick<TransferBoardOrder, "lines" | "allocations">,
): Map<number, number> {
  const totals = summarizeTransferAllocations(order as TransferBoardOrder);
  const packedByItemId = new Map<number, number>();
  for (const line of order.lines) {
    packedByItemId.set(line.id, totals.get(line.id)?.total_allocated_quantity ?? 0);
  }
  return packedByItemId;
}

export function transferLineCartonSealedAt(
  order: Pick<TransferBoardOrder, "allocations" | "boxes">,
  lineId: number,
): string | null {
  const packListIds = new Set<number>();
  for (const allocation of order.allocations) {
    if (Number(allocation.transfer_order_item_id) !== Number(lineId)) {
      continue;
    }
    packListIds.add(Number(allocation.pack_list_id));
  }
  let latest: string | null = null;
  for (const box of order.boxes) {
    if (!packListIds.has(Number(box.id))) {
      continue;
    }
    const sealedAt = box.sealed_at?.trim() ?? "";
    if (sealedAt === "") {
      continue;
    }
    if (latest == null || sealedAt > latest) {
      latest = sealedAt;
    }
  }
  return latest;
}

export function transferLineSkuPickedAt(
  order: Pick<TransferBoardOrder, "sku_picks">,
  skuId: string | null,
): string | null {
  const sku = skuId?.trim() ?? "";
  if (sku === "") {
    return null;
  }
  let earliest: string | null = null;
  for (const pick of order.sku_picks ?? []) {
    if (pick.sku_id.trim().toLowerCase() !== sku.toLowerCase()) {
      continue;
    }
    const pickedAt = pick.picked_at?.trim() ?? "";
    if (pickedAt === "") {
      continue;
    }
    if (earliest == null || pickedAt < earliest) {
      earliest = pickedAt;
    }
  }
  return earliest;
}

export function transferLineFirstPickedAt(
  order: Pick<TransferBoardOrder, "sku_picks" | "first_picked_at" | "lines">,
  skuId: string | null,
): string | null {
  const skuPickedAt = transferLineSkuPickedAt(order, skuId);
  if (skuPickedAt != null) {
    return skuPickedAt;
  }
  const lineCount = order.lines?.length ?? 0;
  if (lineCount !== 1) {
    return null;
  }
  const cartPickedAt = order.first_picked_at?.trim() ?? "";
  return cartPickedAt === "" ? null : cartPickedAt;
}

export function transferLineFirstPackedAt(
  order: Pick<TransferBoardOrder, "allocations">,
  lineId: number,
): string | null {
  let earliest: string | null = null;
  for (const allocation of order.allocations) {
    if (Number(allocation.transfer_order_item_id) !== Number(lineId)) {
      continue;
    }
    const packedAt = allocation.created_at?.trim() ?? "";
    if (packedAt === "") {
      continue;
    }
    if (earliest == null || packedAt < earliest) {
      earliest = packedAt;
    }
  }
  return earliest;
}

export function transferOrderHasShipmentEvents(
  order: Pick<
    TransferBoardOrder,
    | "allocations"
    | "shipped_at"
    | "delivered_at"
    | "received_at"
    | "packing_started_at"
    | "first_picked_at"
    | "sku_picks"
    | "shipped_notification_url"
    | "shipped_notification_freshdesk_ticket_id"
  >,
): boolean {
  if ((order.first_picked_at?.trim() ?? "") !== "") {
    return true;
  }
  if ((order.sku_picks?.length ?? 0) > 0) {
    return true;
  }
  if ((order.packing_started_at?.trim() ?? "") !== "") {
    return true;
  }
  if (order.allocations.some((allocation) => (allocation.created_at?.trim() ?? "") !== "")) {
    return true;
  }
  if ((order.shipped_at?.trim() ?? "") !== "") {
    return true;
  }
  if ((order.delivered_at?.trim() ?? "") !== "") {
    return true;
  }
  if ((order.received_at?.trim() ?? "") !== "") {
    return true;
  }
  if ((order.shipped_notification_url?.trim() ?? "") !== "") {
    return true;
  }
  return (order.shipped_notification_freshdesk_ticket_id ?? 0) > 0;
}

/** Received column on the transfer lines table: green when complete, red when short/over, black when zero. */
export function transferLineReceivedQuantityClassName(
  receivedQuantity: number,
  packedQuantity: number,
): string {
  if (receivedQuantity === 0) {
    return "text-slate-900";
  }
  if (receivedQuantity === packedQuantity) {
    return "font-medium text-emerald-700";
  }
  return "font-medium text-rose-700";
}

/** Allocation rolled up per (item, box) — convenient view for the selected-order panel. */
export interface TransferAllocationSummary {
  /** transfer_order_item_id */
  item_id: number;
  /** Total quantity allocated for this item across all boxes. */
  total_allocated_quantity: number;
  /** quantity remaining (line quantity − total allocated). */
  unallocated_quantity: number;
  per_box: { pack_list_id: number; allocation_id: string; quantity: number }[];
}

export function summarizeTransferAllocations(order: TransferBoardOrder): Map<number, TransferAllocationSummary> {
  const itemTotals = new Map<number, TransferAllocationSummary>();
  for (const line of order.lines) {
    const packTarget = effectiveTransferPackQuantity(line);
    itemTotals.set(line.id, {
      item_id: line.id,
      total_allocated_quantity: 0,
      unallocated_quantity: packTarget,
      per_box: [],
    });
  }
  for (const allocation of order.allocations) {
    const summary = itemTotals.get(allocation.transfer_order_item_id);
    if (summary == null) continue;
    summary.total_allocated_quantity += allocation.quantity;
    const line = order.lines.find((entry) => entry.id === allocation.transfer_order_item_id);
    const packTarget = line != null ? effectiveTransferPackQuantity(line) : summary.unallocated_quantity;
    summary.unallocated_quantity = Math.max(0, packTarget - summary.total_allocated_quantity);
    summary.per_box.push({
      pack_list_id: allocation.pack_list_id,
      allocation_id: allocation.id,
      quantity: allocation.quantity,
    });
  }
  return itemTotals;
}
